import { gostSesija } from '../auth/gost'
import { staffSesija } from '../auth/session'
import type { SessionStore } from '../auth/store'
import { pamtiPolaziste } from '../lib/polaziste'
import { raw, type RawZahtjev } from './http'
import { ApiError } from './problem'

/**
 * Autentificirani pozivi. Jedina razlika prema `http.ts`: nose access token i
 * na 401 JEDNOM pokušaju osvježiti sesiju pa ponoviti zahtjev.
 *
 * Zašto samo jednom: drugi 401 nakon svježeg tokena nije istek nego odbijanje
 * (ugašen resort, deaktiviran račun, blokiran boravak), a ponavljanje bi bilo
 * petlja koja tuče poslužitelja. 403 se NE ponavlja — to je odluka, ne istek;
 * posebno `password_change_required`, koji vrijedi dok se lozinka ne promijeni.
 *
 * Klijent je VEZAN uz jednu sesiju: `api` nosi token osoblja, `apiGost` token
 * gosta. Bez toga bi zahtjev gosta u pregledniku recepcionera povukao token one
 * sesije koja je zadnja pisala u modul (zadatak 7 §2).
 */

let jezik: string | undefined

/** Postavlja `Accept-Language` za sve buduće pozive (mijenja ga izbornik jezika). */
export function postaviJezikZahtjeva(novi: string): void {
  jezik = novi
}

// Zapamćen jezik je procesno stanje: test koji ga postavi ne smije ga ostaviti
// sljedećem (D15-A §1a).
pamtiPolaziste(() => {
  jezik = undefined
})

export interface ApiKlijent {
  get: <T>(putanja: string, signal?: AbortSignal) => Promise<T>
  post: <T>(putanja: string, tijelo?: unknown, zaglavlja?: Record<string, string>) => Promise<T>
  /**
   * `multipart/form-data` (dotjerivanje-12 §1) — slanje slike.
   *
   * Kroz OVAJ sloj, a ne golim `fetch`-om kao što je do sada radio upload
   * kampanja: gostovi pozivi idu isključivo `apiGost`-om (čuvar
   * `api/gost.klijent.test.ts`), a to nije stilsko pravilo nego zakon "svaki
   * gostov zapis nosi boravak-id" — tim tokenom putuje boravak, pa poslužitelj
   * ima što žigosati. Usput se dobiva i osvježavanje sesije na 401, koje uploadu
   * treba jednako kao i svakom drugom pozivu.
   */
  postForm: <T>(putanja: string, obrazac: FormData) => Promise<T>
  put: <T>(putanja: string, tijelo?: unknown) => Promise<T>
  del: (putanja: string) => Promise<void>
}

export function stvoriKlijenta(sesija: SessionStore): ApiKlijent {
  async function posalji<T>(putanja: string, zahtjev: RawZahtjev): Promise<T> {
    const potroseni = sesija.getAccessToken()
    const opcije: RawZahtjev = { ...zahtjev, jezik, token: potroseni }

    try {
      return await raw<T>(putanja, opcije)
    } catch (greska) {
      if (!(greska instanceof ApiError) || greska.status !== 401) throw greska

      // Token s kojim je zahtjev pao ide uz zahtjev za osvježavanje: je li ga
      // netko u međuvremenu već zamijenio, rotacije nema (vidi `auth/store.ts`).
      const svjez = await sesija.osvjezi(potroseni)
      if (!svjez || svjez === potroseni) throw greska

      return raw<T>(putanja, { ...opcije, token: svjez })
    }
  }

  return {
    get: <T>(putanja: string, signal?: AbortSignal) => posalji<T>(putanja, { metoda: 'GET', signal }),

    post: <T>(putanja: string, tijelo?: unknown, zaglavlja?: Record<string, string>) =>
      posalji<T>(putanja, { metoda: 'POST', tijelo, dodatnaZaglavlja: zaglavlja }),

    postForm: <T>(putanja: string, obrazac: FormData) =>
      posalji<T>(putanja, { metoda: 'POST', obrazac }),

    put: <T>(putanja: string, tijelo?: unknown) => posalji<T>(putanja, { metoda: 'PUT', tijelo }),

    del: (putanja: string) => posalji<void>(putanja, { metoda: 'DELETE' }),
  }
}

/** Konzola osoblja (`/admin/*`, `/sys/*`). */
export const api = stvoriKlijenta(staffSesija)

/** Aplikacija gosta (`/me/*`, `/bookings*`, `/stations`, `/valet`). */
export const apiGost = stvoriKlijenta(gostSesija)
